import { useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { useToast } from "@/features/toast/ToastProvider";
import { useConfirm } from "@/features/confirm/ConfirmProvider";
import { todayLocalDate } from "@/lib/date";
import { formatDate } from "@bookboost/shared/format";

interface FxRow {
  id: string;
  currency: string;
  rate_vs_base: number;
  effective_date: string;
  notes: string | null;
  created_at: string;
}

interface FxResponse {
  base: string;
  currencies: Array<{ currency: string; history: FxRow[] }>;
  missing: string[];
}

export const Route = createFileRoute("/admin/fx")({
  component: FxPage,
});

function FxPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "fx"],
    queryFn: () => api.get<FxResponse>("/api/admin/fx"),
  });

  const addRate = useMutation({
    mutationFn: (body: { currency: string; rate_vs_base: number; effective_date: string; notes: string | null }) =>
      api.post("/api/admin/fx", body),
    onSuccess: (_, vars) => {
      toast.ok(`Saved 1 ${data?.base ?? "EUR"} = ${vars.rate_vs_base} ${vars.currency}.`);
      qc.invalidateQueries({ queryKey: ["admin", "fx"] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't save the rate."),
  });

  const deleteRate = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/fx/${id}`),
    onSuccess: () => {
      toast.ok("Rate deleted.");
      qc.invalidateQueries({ queryKey: ["admin", "fx"] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't delete."),
  });

  if (isLoading) return <p className="lede">Loading FX rates…</p>;
  if (error || !data) {
    return (
      <Panel variant="err">
        <h2>FX rates unavailable</h2>
        <p>{error instanceof Error ? error.message : "Failed to load."}</p>
      </Panel>
    );
  }

  const todayIso = todayLocalDate();

  return (
    <>
      <h1>FX rates</h1>

      <Panel title={`Base currency · ${data.base}`}>
        <p className="muted">
          Rates are stored as <code>1 {data.base} = rate &lt;currency&gt;</code>. Cross-currency conversion (e.g. USD →
          GBP) pivots through {data.base} mathematically: <code>amountB = amountA × (rateB / rateA)</code>. The
          destination is whatever you ask for; {data.base} is the storage model, not a forced output. Every currency you
          use needs a rate row.
        </p>
      </Panel>

      {data.missing.length > 0 && (
        <Panel variant="warn" title="⚠ Missing FX rates for currencies in use">
          <p className="muted">
            These currencies appear in deals, invoices, or rep salary settings but don&apos;t have a rate row yet.
            Conversions involving them will fail until you add one:
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            {data.missing.map((c) => (
              <MissingRateForm
                key={c}
                currency={c}
                base={data.base}
                todayIso={todayIso}
                onSubmit={(rate) =>
                  addRate.mutate({
                    currency: c,
                    rate_vs_base: rate,
                    effective_date: todayIso,
                    notes: null,
                  })
                }
                disabled={addRate.isPending}
              />
            ))}
          </div>
        </Panel>
      )}

      <Panel title="Add / update rate">
        <AddRateForm
          base={data.base}
          todayIso={todayIso}
          onSubmit={(payload) => addRate.mutate(payload)}
          disabled={addRate.isPending}
        />
        <p className="muted" style={{ marginTop: 8, fontSize: 12 }}>
          Saving the same currency + date replaces the existing row (useful if you mis-typed). Setting a new date creates
          a new version — older history stays intact so historical commissions could re-price at the right rate.
        </p>
      </Panel>

      <Panel title="Current rates">
        {data.currencies.length === 0 ? (
          <p className="muted">No FX rates yet. Add one above.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Currency</th>
                <th>Current rate (1 {data.base} = ?)</th>
                <th>Effective from</th>
                <th>Inverse (1 {"<currency>"} = ? {data.base})</th>
                <th>Notes</th>
                <th>History</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.currencies.map(({ currency, history }) => {
                const [current, ...older] = history;
                if (!current) return null;
                const inverse = (1 / Number(current.rate_vs_base)).toFixed(6);
                return (
                  <tr key={currency}>
                    <td><strong>{currency}</strong></td>
                    <td>{Number(current.rate_vs_base)}</td>
                    <td className="muted">{formatDate(current.effective_date)}</td>
                    <td className="muted">{inverse}</td>
                    <td className="muted" style={{ fontSize: 12 }}>{current.notes ?? "—"}</td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {older.length === 0 ? "—" : older.map((h) => `${h.rate_vs_base} (${h.effective_date})`).join(" · ")}
                    </td>
                    <td>
                      <button
                        type="button"
                        style={{ color: "var(--err)", borderColor: "var(--err)" }}
                        disabled={deleteRate.isPending}
                        onClick={async () => {
                          const ok = await confirm({
                            prompt: `Delete the ${currency} rate row from ${formatDate(current.effective_date)}?`,
                            tone: "destructive",
                            confirmLabel: "Delete",
                          });
                          if (ok) deleteRate.mutate(current.id);
                        }}
                      >
                        delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Panel>
    </>
  );
}

function AddRateForm({
  base,
  todayIso,
  onSubmit,
  disabled,
}: {
  base: string;
  todayIso: string;
  onSubmit: (payload: { currency: string; rate_vs_base: number; effective_date: string; notes: string | null }) => void;
  disabled: boolean;
}) {
  const [currency, setCurrency] = useState("");
  const [rate, setRate] = useState("");
  const [effective, setEffective] = useState(todayIso);
  const [notes, setNotes] = useState("");

  function submit(e: FormEvent) {
    e.preventDefault();
    const rateNum = Number(rate);
    if (!Number.isFinite(rateNum) || rateNum <= 0) return;
    onSubmit({
      currency: currency.trim().toUpperCase(),
      rate_vs_base: rateNum,
      effective_date: effective,
      notes: notes.trim() || null,
    });
    setCurrency("");
    setRate("");
    setNotes("");
  }

  return (
    <form
      onSubmit={submit}
      style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr 1.5fr 2fr auto", gap: 8, alignItems: "end" }}
    >
      <label>
        Currency
        <input
          required
          maxLength={3}
          pattern="[A-Za-z]{3}"
          placeholder="GBP"
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          style={{ textTransform: "uppercase" }}
        />
      </label>
      <label>
        Rate (1 {base} = ?)
        <input
          required
          type="number"
          step="0.00000001"
          min="0.00000001"
          placeholder="0.85"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
        />
      </label>
      <label>
        Effective from
        <input required type="date" value={effective} onChange={(e) => setEffective(e.target.value)} />
      </label>
      <label>
        Notes (optional)
        <input
          placeholder="e.g. ECB reference rate, manual update"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </label>
      <button type="submit" className="primary" disabled={disabled}>
        Save
      </button>
    </form>
  );
}

function MissingRateForm({
  currency,
  base,
  todayIso,
  onSubmit,
  disabled,
}: {
  currency: string;
  base: string;
  todayIso: string;
  onSubmit: (rate: number) => void;
  disabled: boolean;
}) {
  const [rate, setRate] = useState("");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const n = Number(rate);
        if (Number.isFinite(n) && n > 0) onSubmit(n);
      }}
      style={{
        display: "inline-flex",
        gap: 6,
        alignItems: "center",
        background: "var(--surface)",
        border: "1px solid var(--hairline)",
        padding: "4px 8px",
        borderRadius: 6,
      }}
    >
      <strong>{currency}</strong>
      <span className="muted" style={{ fontSize: 11 }}>1 {base} =</span>
      <input
        type="number"
        step="0.00000001"
        min="0.00000001"
        required
        placeholder="?"
        value={rate}
        onChange={(e) => setRate(e.target.value)}
        style={{ width: 100 }}
        title={`Effective from ${todayIso}`}
      />
      <button type="submit" className="primary" style={{ padding: "4px 10px" }} disabled={disabled}>
        add
      </button>
    </form>
  );
}
